import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import { showUndoToast } from "./showUndoToast";

import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { buildPreviewSchema } from "./trainingBlockSpecs";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import {
  API_get_learning_section_detail,
  API_get_training_badges,
} from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import Select from "@mui/material/Select";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ModuleEditorModal from "./ModuleEditorModal";
import SectionInputsModal2 from "./SectionInputsModal2";
import DeleteWarningModal from "./DeleteWarningModal";
import PublishTrackModal from "./PublishTrackModal";
import RoleAssignmentChips from "./RoleAssignmentChips";
import LoadSpinner from "../../../utilities/LoadSpinner";
import SectionPreview from "./SectionPreview";
import TestOutSkipsNote from "./TestOutSkipsNote";
import { uploadLearningFile } from "./swipeDeckHelpers";
import LearningContentPreviewModal from "./LearningContentPreviewModal";
import LearningPreviewMenuButton from "./LearningPreviewMenuButton";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const modalStyle = {
  // Center without `transform` — @hello-pangea/dnd's drag clone uses
  // `position: fixed`, which becomes relative to the nearest transformed
  // ancestor instead of the viewport. With a transformed modal wrapping a
  // scrollable drag list, dragging offsets the ghost by the scroll amount.
  // `inset: 0 + margin: auto` centers identically without a transform.
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "96%",
  height: "94%",
  maxWidth: "1600px",
  maxHeight: "94vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

function TrackEditorModal(props) {
  const isEdit = Boolean(props.modalDataTrack?.uuid);

  const [showMenu, setShowMenu] = useState(true);
  const [showSettings, setShowSettings] = useState(!isEdit);
  const [openPublish, setOpenPublish] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");

  // NULL status (legacy tracks) is treated as already-live; only an
  // explicit "draft" is unpublished. Saved drafts can be published.
  const isDraft = props.modalDataTrack?.status === "draft";

  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [selectedSectionUUID, setSelectedSectionUUID] = useState();
  const [isLoadingSection, setIsLoadingSection] = useState(false);

  const [expandedModuleIndices, setExpandedModuleIndices] = useState(new Set());
  const [hoveredModuleIndex, setHoveredModuleIndex] = useState(null);
  const [hoveredSectionKey, setHoveredSectionKey] = useState(null);

  // Drill-in modals
  const [openModuleEditor, setOpenModuleEditor] = useState(false);
  const [openSectionInputsModal, setOpenSectionInputsModal] = useState(false);
  const [isCreatingNewModule, setIsCreatingNewModule] = useState(false);
  const [isCreatingNewSection, setIsCreatingNewSection] = useState(false);
  const [targetModuleForNewSection, setTargetModuleForNewSection] = useState(null);

  // Add-module menu / dialog
  const [addModuleAnchor, setAddModuleAnchor] = useState(null);
  const [openAddExistingDialog, setOpenAddExistingDialog] = useState(false);
  const [pendingExistingNames, setPendingExistingNames] = useState([]);
  const [addExistingInput, setAddExistingInput] = useState("");
  const [addSectionAnchor, setAddSectionAnchor] = useState(null);
  const [targetModuleForSectionAdd, setTargetModuleForSectionAdd] =
    useState(null);
  const [openAddExistingSectionsDialog, setOpenAddExistingSectionsDialog] =
    useState(false);
  const [pendingExistingSections, setPendingExistingSections] =
    useState([]);
  const [addExistingSectionInput, setAddExistingSectionInput] = useState("");

  const [openDeleteWarning, setOpenDeleteWarning] = useState(false);

  const [uploadingCover, setUploadingCover] = useState(false);

  // Due-date mode for the track: "none" | "fixed" | "relative". Derived from the
  // data but held locally so editing a value doesn't flip the mode (e.g. clearing
  // the days input). Re-synced each time the editor opens (and when a different
  // track loads). Keyed on props.open too, not just uuid: new tracks all share
  // uuid === null and this component stays mounted, so without the open dep,
  // reopening "New Track" after picking relative mode would keep the stale mode
  // while modalDataTrack has reset to null — saving would then create a track
  // with no due date despite the visible mode.
  const [dueMode, setDueMode] = useState("none");
  // Last valid days value, so clearing the input then leaving/saving restores it
  // rather than silently dropping the relative due date (see handleDaysBlur).
  const lastValidDaysRef = useRef(14);
  useEffect(() => {
    if (!props.open) return;
    const relDays = props.modalDataTrack?.completion_due_days_after_enrollment;
    // Reset the fallback per track so a previously-edited relative track's value
    // can't leak into this one (default 14 when this track has no offset).
    lastValidDaysRef.current = relDays ?? 14;
    if (relDays != null) {
      setDueMode("relative");
    } else if (props.modalDataTrack?.completion_due_date != null) {
      setDueMode("fixed");
    } else {
      setDueMode("none");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.modalDataTrack?.uuid]);

  // Snapshot for cancel-revert
  const originalTrackRef = useRef(null);

  // Token guards against stale section fetches resolving out of order
  // when the user clicks rapidly between sections / modules.
  const sectionFetchIdRef = useRef(0);

  const modules = props.modalDataTrack?.modules || [];
  const moduleCount = modules.length;

  // Mirror the latest track data so the Undo toast's callback can read current
  // state synchronously. The toast closure captures props from the render when
  // the section was removed (stale), and a setState updater runs too late to
  // synchronously yield the rebuilt module for the immediate re-POST.
  const modalDataTrackRef = useRef(props.modalDataTrack);
  useEffect(() => {
    modalDataTrackRef.current = props.modalDataTrack;
  }, [props.modalDataTrack]);

  // Same reason: the undo toasts need the CURRENT selection to decide whether
  // restoring shifts/occupies the selected module or section slot.
  const selectedModuleIndexRef = useRef(selectedModuleIndex);
  useEffect(() => {
    selectedModuleIndexRef.current = selectedModuleIndex;
  }, [selectedModuleIndex]);
  const selectedSectionIndexRef = useRef(selectedSectionIndex);
  useEffect(() => {
    selectedSectionIndexRef.current = selectedSectionIndex;
  }, [selectedSectionIndex]);

  // Pending Undo-toast ids, dismissed whenever the editor closes (any path:
  // cancel, save, X, backdrop, unmount). After close, handleClose reverts local
  // state to the pre-edit snapshot, so a lingering Undo can no longer tell a
  // persisted removal from a restored one (its dedup guard would misfire and
  // silently no-op), and a stale Undo clicked after reopening could act against
  // the wrong track.
  const undoToastIdsRef = useRef([]);
  useEffect(() => {
    if (!props.open) {
      undoToastIdsRef.current.forEach((id) => toast.dismiss(id));
      undoToastIdsRef.current = [];
    }
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      originalTrackRef.current = props.modalDataTrack;
      // Auto-expand the first module so the first section is reachable.
      setExpandedModuleIndices(new Set([0]));
      // Settings panel: open by default for new tracks (where the user
      // is likely to fill them in), closed by default for edits.
      setShowSettings(!isEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Section preview props (passed to the keyed <SectionPreview> in render)

  const quizzesData = props.quizzesData;
  const homeCompany = props.homeCompany;
  const previewLanguage = props.language;

  // Read-only schema for the section preview. Memoized so the per-section
  // <SectionPreview> editor gets a stable schema for its lifetime.
  const previewSchema = useMemo(
    () => buildPreviewSchema({ homeCompany, quizzesData, previewLanguage }),
    [homeCompany, quizzesData, previewLanguage]
  );

  // The section preview is now a self-contained, keyed <SectionPreview> that
  // mounts a fresh editor per section (see render + module-level component
  // above). There is no longer a shared preview editor for the parent to
  // clear, so removeAllBlocks is a no-op kept only so the many existing
  // callers and the SectionInputsModal2 prop don't need to change. Switching
  // sections / reopening the modal can no longer leave stale blocks behind,
  // because each section renders its own editor seeded via initialContent.
  const removeAllBlocks = () => {};


  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Section detail fetcher

  const fetchSectionDetail = async (showLoading, learning_section_uuid) => {
    setIsLoadingSection(showLoading);
    const myFetchId = ++sectionFetchIdRef.current;
    const api_params = {
      company_id: props.homeCompany,
      learning_section_uuid: learning_section_uuid,
    };
    try {
      const res = await getRightworkAPI(
        API_get_learning_section_detail,
        api_params
      );

      // Drop stale fetches so older responses can't overwrite newer ones.
      if (sectionFetchIdRef.current !== myFetchId) return undefined;

      let mapSignedUrlToFileUuid =
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
      mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);
      const rawBlocksAsJson = res.data.redux?.payload?.learning_section.as_json;
      rawBlocksAsJson.forEach((block) => {
        const validTypes = ["image", "video", "audio", "file", "pdf"];
        if (!validTypes.includes(block.type)) return;
        mapSignedUrlToFileUuid[block.props.url] = block.props.file_uuid;
      });
      localStorage.setItem(
        "mapSignedUrlToFileUuid",
        JSON.stringify(mapSignedUrlToFileUuid)
      );

      if (res.status === 200) {
        const sectionData = res.data.redux?.payload?.learning_section;
        props.setModalDataSection(sectionData);
        setIsLoadingSection(false);
        return sectionData;
      }
    } catch (error) {
      if (sectionFetchIdRef.current === myFetchId) {
        setIsLoadingSection(false);
      }
      console.error("fetchSectionDetail failed", error);
    }
  };

  // Auto-load first section on open. NOT including drill-in toggles in
  // the deps — that races the BlockNote editor. Drill-in saves propagate
  // back via props.tracksData → modalDataTrack, so a re-render handles
  // refresh without a manual refetch.
  useEffect(() => {
    if (props.open && modules?.[0]?.sections?.[0]?.uuid) {
      fetchSectionDetail(true, modules[0].sections[0].uuid);
      setSelectedModuleIndex(0);
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(modules[0].sections[0].uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // No manual block-loading effect anymore: <SectionPreview> (rendered below,
  // keyed by the selected section uuid) mounts a fresh editor seeded with the
  // fetched section's blocks. Switching sections remounts it via key; in-place
  // edits re-sync through its own effect. This replaced the old reused-editor
  // load that raced editor.document and threw "Block with ID … not found".

  const selectSection = (moduleIdx, sectionIdx, uuid) => {
    if (isLoadingSection) return;
    setSelectedModuleIndex(moduleIdx);
    setSelectedSectionIndex(sectionIdx);
    setSelectedSectionUUID(uuid);
    removeAllBlocks();
    fetchSectionDetail(true, uuid);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Cross-module navigation helpers

  const findNextModuleWithSections = (startIdx) => {
    for (let i = startIdx + 1; i < modules.length; i++) {
      if (modules[i]?.sections?.length > 0) return i;
    }
    return null;
  };

  const findPrevModuleWithSections = (startIdx) => {
    for (let i = startIdx - 1; i >= 0; i--) {
      if (modules[i]?.sections?.length > 0) return i;
    }
    return null;
  };

  const currentModule = modules[selectedModuleIndex];
  const currentSection = currentModule?.sections?.[selectedSectionIndex];
  const lastSectionInModule =
    currentModule?.sections?.length === selectedSectionIndex + 1;
  const lastSectionInTrack =
    modules.length === selectedModuleIndex + 1 && lastSectionInModule;
  const firstSectionInTrack =
    selectedModuleIndex === 0 && selectedSectionIndex === 0;
  const firstSectionInModule =
    selectedModuleIndex !== 0 && selectedSectionIndex === 0;

  const handlePrev = () => {
    if (firstSectionInTrack) return;
    if (firstSectionInModule) {
      const prevIdx = findPrevModuleWithSections(selectedModuleIndex);
      if (prevIdx === null) return;
      const lastSecIdx = modules[prevIdx].sections.length - 1;
      const uuid = modules[prevIdx].sections[lastSecIdx]?.uuid;
      setExpandedModuleIndices((s) => new Set([...s, prevIdx]));
      selectSection(prevIdx, lastSecIdx, uuid);
    } else {
      const newIdx = selectedSectionIndex - 1;
      const uuid = modules[selectedModuleIndex].sections[newIdx]?.uuid;
      selectSection(selectedModuleIndex, newIdx, uuid);
    }
  };

  const handleNext = () => {
    if (lastSectionInTrack) return;
    if (lastSectionInModule) {
      const nextIdx = findNextModuleWithSections(selectedModuleIndex);
      if (nextIdx === null) return;
      const uuid = modules[nextIdx].sections[0]?.uuid;
      setExpandedModuleIndices((s) => new Set([...s, nextIdx]));
      selectSection(nextIdx, 0, uuid);
    } else {
      const newIdx = selectedSectionIndex + 1;
      const uuid = modules[selectedModuleIndex].sections[newIdx]?.uuid;
      selectSection(selectedModuleIndex, newIdx, uuid);
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Track-level field handlers

  const handleNameChange = (event) => {
    props.setModalDataTrack((prev) => ({ ...prev, name: event.target.value }));
  };

  const handleDescriptionChange = (event) => {
    props.setModalDataTrack((prev) => ({
      ...prev,
      description: event.target.value,
    }));
  };

  const handleDateChange = (newDate) => {
    const dateString = newDate
      ? `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(newDate.getDate()).padStart(2, "0")}`
      : null;
    props.setModalDataTrack((prev) => ({
      ...prev,
      completion_due_date: dateString,
      completion_due_days_after_enrollment: null,
    }));
  };

  // Switch between no-due / fixed-date / relative modes, clearing the field that
  // doesn't apply so the track persists at most one mode.
  const handleDueModeChange = (mode) => {
    setDueMode(mode);
    if (mode === "relative") {
      // Seed the ref with the value we're about to set, so clearing it then
      // blurring restores this track's value — not a stale one carried over from
      // a prior mode toggle or track.
      lastValidDaysRef.current =
        props.modalDataTrack?.completion_due_days_after_enrollment ?? 14;
    }
    props.setModalDataTrack((prev) => ({
      ...prev,
      completion_due_date: mode === "fixed" ? prev.completion_due_date : null,
      completion_due_days_after_enrollment:
        mode === "relative"
          ? prev.completion_due_days_after_enrollment ?? 14
          : null,
    }));
  };

  const handleDaysChange = (event) => {
    const raw = event.target.value;
    // Clamp to [1, 3650] to match the backend cap (PositiveSmallIntegerField).
    const n =
      raw === "" ? null : Math.min(3650, Math.max(1, parseInt(raw, 10) || 1));
    if (n != null) lastValidDaysRef.current = n;
    props.setModalDataTrack((prev) => ({
      ...prev,
      completion_due_days_after_enrollment: n,
      completion_due_date: null,
    }));
  };

  // Clearing the input stores null while typing; if the manager leaves it blank
  // in relative mode, restore the last valid value on blur so saving doesn't send
  // null — which the backend reads as "no due date" and clears both date fields.
  const handleDaysBlur = () => {
    if (
      dueMode === "relative" &&
      props.modalDataTrack?.completion_due_days_after_enrollment == null
    ) {
      props.setModalDataTrack((prev) => ({
        ...prev,
        completion_due_days_after_enrollment: lastValidDaysRef.current,
        completion_due_date: null,
      }));
    }
  };

  const handleCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploadingCover(true);
    try {
      const { uuid, url } = await uploadLearningFile(props.homeCompany, file);
      props.setModalDataTrack((prev) => ({
        ...prev,
        cover: { uuid, url },
      }));
    } catch (err) {
      toast.error(err?.message || "Cover upload failed. Please try again.");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverClear = () => {
    props.setModalDataTrack((prev) => ({ ...prev, cover: null }));
  };

  const handleRolesChange = (newRoleIds) => {
    const roleObjects = newRoleIds
      .map((id) => (props.roleNames || []).find((r) => r.id === id))
      .filter(Boolean);
    props.setModalDataTrack((prev) => ({
      ...prev,
      roles_assigned_to: roleObjects,
    }));
  };

  const selectedRoleIds = useMemo(
    () => (props.modalDataTrack?.roles_assigned_to || []).map((r) => r.id),
    [props.modalDataTrack?.roles_assigned_to]
  );

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Badge awarded on track completion (lms_info.TrainingBadge)

  const [badgeOptions, setBadgeOptions] = useState([]);

  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    const fetchBadges = async () => {
      const res = await getRightworkAPI(API_get_training_badges, {
        company_id: props.homeCompany,
      });
      if (cancelled) return;
      if (res.status === 200) {
        const catalog = res.data?.redux?.payload?.training_badges || [];
        // Only active badges are offerable; a stored-but-archived mapping is
        // labeled via the serializer's grants_badge.archived flag below.
        setBadgeOptions(catalog.filter((b) => !b.archived_at));
      }
    };
    fetchBadges();
    return () => {
      cancelled = true;
    };
  }, [props.open, props.homeCompany]);

  const grantsBadge = props.modalDataTrack?.grants_badge || null;

  const handleGrantsBadgeChange = (event) => {
    const badgeUuid = event.target.value;
    const option = badgeOptions.find((b) => b.uuid === badgeUuid);
    // Deliberate field-by-field mapping, NOT a spread: the catalog shape
    // (get_training_badges: archived_at, granting_track_count, ...) differs
    // from the shape modalDataTrack.grants_badge round-trips through
    // LearningTrackDetailGetSerializer.get_grants_badge ({uuid, name, color,
    // validity_days, archived}). Keep this in lockstep with that serializer.
    props.setModalDataTrack((prev) => ({
      ...prev,
      grants_badge: option
        ? {
            uuid: option.uuid,
            name: option.name,
            color: option.color,
            validity_days: option.validity_days,
            archived: false,
          }
        : null,
    }));
  };

  // Recertification caution: completing ANY granting track renews the badge,
  // so a second, weaker track lowers the effective recert bar. The catalog
  // count may include this track's own saved mapping, so only warn when the
  // badge is clearly shared (count > 1).
  const badgeSharedAcrossTracks = React.useMemo(() => {
    const selected = props.modalDataTrack?.grants_badge;
    if (!selected) return false;
    const option = badgeOptions.find((b) => b.uuid === selected.uuid);
    return (option?.granting_track_count || 0) > 1;
  }, [badgeOptions, props.modalDataTrack?.grants_badge]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Module list mutations (queued locally; persisted on Save)

  const handleModuleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(modules);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    props.setModalDataTrack((prev) => ({ ...prev, modules: reordered }));

    // Keep selected module pointing at the same module after the drag.
    const sel = selectedModuleIndex;
    let newSel = sel;
    if (sel === result.source.index) {
      newSel = result.destination.index;
    } else if (
      result.source.index < sel &&
      result.destination.index >= sel
    ) {
      newSel = sel - 1;
    } else if (
      result.source.index > sel &&
      result.destination.index <= sel
    ) {
      newSel = sel + 1;
    }
    setSelectedModuleIndex(newSel);

    // Translate expanded set to new indices.
    setExpandedModuleIndices((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        let mapped = i;
        if (i === result.source.index) mapped = result.destination.index;
        else if (
          result.source.index < i &&
          result.destination.index >= i
        )
          mapped = i - 1;
        else if (
          result.source.index > i &&
          result.destination.index <= i
        )
          mapped = i + 1;
        next.add(mapped);
      });
      return next;
    });
  };

  // Re-insert a module removed via handleRemoveModuleFromTrack at its original
  // index. Module removal is local (staged until Save Track), so the undo is a
  // local splice — no re-post needed.
  const undoRemoveModuleFromTrack = (index, removedModule) => {
    const currentModules = modalDataTrackRef.current?.modules || [];
    // Already back (e.g. re-added via "Add existing module") — skip, so we
    // don't stage a duplicate module UUID into the track.
    if (currentModules.some((m) => m?.uuid && m.uuid === removedModule?.uuid)) {
      return;
    }
    // Clamp: the list may have shrunk since removal.
    const insertAt = Math.min(index, currentModules.length);

    props.setModalDataTrack((prev) => {
      const restored = [...(prev.modules || [])];
      restored.splice(insertAt, 0, removedModule);
      return { ...prev, modules: restored };
    });

    // Reverse the selection move handleRemoveModuleFromTrack made, using the
    // CURRENT selection (the user may have navigated during the toast window).
    const currentSelected = selectedModuleIndexRef.current;
    if (insertAt === currentSelected) {
      // The restored module now occupies the selected slot, but the preview
      // still shows the fallback module's section — load the restored module's
      // first section so highlight and preview agree.
      const firstUuid = removedModule?.sections?.[0]?.uuid;
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(firstUuid);
      removeAllBlocks();
      if (firstUuid) fetchSectionDetail(true, firstUuid);
    } else if (insertAt < currentSelected) {
      // Insertion pushed the selected module down one — keep the highlight on it.
      setSelectedModuleIndex(currentSelected + 1);
    }
  };

  const handleRemoveModuleFromTrack = (index) => {
    // Capture before filtering so the undo toast can restore it in place.
    const removedModule = modules[index];
    const updated = modules.filter((_, i) => i !== index);
    props.setModalDataTrack((prev) => ({ ...prev, modules: updated }));

    if (removedModule) {
      const toastId = showUndoToast("Module removed from track", () =>
        undoRemoveModuleFromTrack(index, removedModule)
      );
      undoToastIdsRef.current.push(toastId);
    }

    if (updated.length === 0) {
      setSelectedModuleIndex(0);
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(undefined);
      removeAllBlocks();
      return;
    }
    if (index === selectedModuleIndex) {
      const newModuleIdx = Math.min(selectedModuleIndex, updated.length - 1);
      const firstUuid = updated[newModuleIdx]?.sections?.[0]?.uuid;
      setSelectedModuleIndex(newModuleIdx);
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(firstUuid);
      removeAllBlocks();
      if (firstUuid) fetchSectionDetail(true, firstUuid);
    } else if (index < selectedModuleIndex) {
      setSelectedModuleIndex(selectedModuleIndex - 1);
    }
  };

  const handleAddExistingModulesConfirm = () => {
    const newModules = pendingExistingNames
      .map((name) => props.modulesData?.find((m) => m.name === name))
      .filter(Boolean);

    const existingUuids = new Set(modules.map((m) => m.uuid));
    const toAdd = newModules.filter((m) => !existingUuids.has(m.uuid));

    if (toAdd.length === 0) {
      setOpenAddExistingDialog(false);
      setPendingExistingNames([]);
      return;
    }

    props.setModalDataTrack((prev) => ({
      ...prev,
      modules: [...(prev.modules || []), ...toAdd],
    }));
    setOpenAddExistingDialog(false);
    setPendingExistingNames([]);
    setAddExistingInput("");
  };

  const handleNewModuleCreated = async (newModule) => {
    const updatedTrack = {
      ...props.modalDataTrack,
      modules: [...(props.modalDataTrack.modules || []), newModule],
    };
    props.setModalDataTrack(updatedTrack);
    await props.post_learning_track(updatedTrack);

    const freshTrack = props.tracksData?.find(
      (t) => t.uuid === updatedTrack.uuid
    );
    const hasNewModule = freshTrack?.modules?.some(
      (m) => m.uuid === newModule.uuid
    );
    if (freshTrack && hasNewModule) {
      props.setModalDataTrack(freshTrack);
    }

    const newModuleIndex = updatedTrack.modules.length - 1;
    setSelectedModuleIndex(newModuleIndex);
    setExpandedModuleIndices((s) => new Set([...s, newModuleIndex]));
    if (newModule.sections?.length > 0) {
      const firstUuid = newModule.sections[0].uuid;
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(firstUuid);
      removeAllBlocks();
      await fetchSectionDetail(true, firstUuid);
    }
    setIsCreatingNewModule(false);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Module / section drill-in handlers

  const handleEditModule = (module, moduleIdx) => {
    removeAllBlocks();
    props.setModalDataModule(module);
    setIsCreatingNewModule(false);
    setOpenModuleEditor(true);
  };

  const handleEditSection = (section, moduleIdx, sectionIdx) => {
    removeAllBlocks();
    setSelectedModuleIndex(moduleIdx);
    setSelectedSectionIndex(sectionIdx);
    setSelectedSectionUUID(section.uuid);
    fetchSectionDetail(true, section.uuid);
    setIsCreatingNewSection(false);
    setOpenSectionInputsModal(true);
  };

  const handleCreateSectionInModule = (module, moduleIdx) => {
    props.setModalDataSection(props.default_data_section);
    setTargetModuleForNewSection({ module, moduleIndex: moduleIdx });
    setIsCreatingNewSection(true);
    setOpenSectionInputsModal(true);
  };

  const handleNewSectionCreatedInModule = async (newSection) => {
    if (!targetModuleForNewSection) return;
    const { module, moduleIndex } = targetModuleForNewSection;

    const updatedModule = {
      ...module,
      sections: [...(module.sections || []), newSection],
    };
    await props.post_learning_module(updatedModule);

    const updatedModules = modules.map((m, i) =>
      i === moduleIndex ? updatedModule : m
    );
    props.setModalDataTrack((prev) => ({ ...prev, modules: updatedModules }));

    const newSectionIndex = updatedModule.sections.length - 1;
    setSelectedModuleIndex(moduleIndex);
    setSelectedSectionIndex(newSectionIndex);
    setSelectedSectionUUID(newSection.uuid);
    setExpandedModuleIndices((s) => new Set([...s, moduleIndex]));
    removeAllBlocks();
    await fetchSectionDetail(true, newSection.uuid);

    setIsCreatingNewSection(false);
    setTargetModuleForNewSection(null);
  };

  const handleAddExistingSectionsToModuleConfirm = async () => {
    if (!targetModuleForSectionAdd) return;
    const { module, moduleIndex } = targetModuleForSectionAdd;
    const newSections = pendingExistingSections.filter(Boolean);

    const existingUuids = new Set((module.sections || []).map((s) => s.uuid));
    const toAdd = newSections.filter((s) => !existingUuids.has(s.uuid));

    if (toAdd.length === 0) {
      setOpenAddExistingSectionsDialog(false);
      setPendingExistingSections([]);
      setAddExistingSectionInput("");
      setTargetModuleForSectionAdd(null);
      return;
    }

    const updatedModule = {
      ...module,
      sections: [...(module.sections || []), ...toAdd],
    };
    await props.post_learning_module(updatedModule);

    const updatedModules = modules.map((m, i) =>
      i === moduleIndex ? updatedModule : m
    );
    props.setModalDataTrack((prev) => ({ ...prev, modules: updatedModules }));

    const firstAddedIndex = (module.sections || []).length;
    const firstAddedSection = toAdd[0];
    setSelectedModuleIndex(moduleIndex);
    setSelectedSectionIndex(firstAddedIndex);
    setSelectedSectionUUID(firstAddedSection?.uuid);
    setExpandedModuleIndices((s) => new Set([...s, moduleIndex]));
    removeAllBlocks();
    if (firstAddedSection?.uuid) {
      await fetchSectionDetail(true, firstAddedSection.uuid);
    }

    setOpenAddExistingSectionsDialog(false);
    setPendingExistingSections([]);
    setAddExistingSectionInput("");
    setTargetModuleForSectionAdd(null);
  };

  const refresh_section = async () => {
    if (!selectedSectionUUID) return;
    const updated = await fetchSectionDetail(true, selectedSectionUUID);
    if (!updated) return;
    // Sidebar rows show the test-out link, so it can't wait for the track refetch.
    props.setModalDataTrack((prev) => ({
      ...prev,
      modules: (prev?.modules || []).map((m) => ({
        ...m,
        sections: (m?.sections || []).map((section) =>
          section.uuid === selectedSectionUUID
            ? { ...section, test_out_quiz: updated.test_out_quiz }
            : section
        ),
      })),
    }));
  };

  // Re-attach a section removed via handleRemoveSectionFromModuleInTrack,
  // restoring its original position. Removal persisted immediately, so the undo
  // re-posts the module too.
  //
  // Build the restored list from the CURRENT module state (prev.modules[moduleIdx]),
  // not a snapshot captured at removal time: the manager may have removed/added
  // other sections in this module before clicking Undo, and post_learning_module
  // replaces the whole section order — persisting a stale snapshot would resurrect
  // a later-deleted section or drop a later-added one.
  const undoRemoveSectionFromModuleInTrack = async (
    moduleUuid,
    sectionIdx,
    removedSection
  ) => {
    // Read current state from the ref (synchronous + up to date), NOT from a
    // setState updater — the updater runs after this function returns, so we
    // couldn't capture the rebuilt module in time to POST it (that skipped the
    // persist, so undo only changed local state and the removal stuck on reopen).
    //
    // Find the module by UUID, not the index captured at removal time: dragging
    // modules in the sidebar reorders prev.modules, so a stale index would
    // restore the section into (and POST) the wrong module.
    if (!moduleUuid) return; // can't safely match without a UUID
    const currentModules = modalDataTrackRef.current?.modules || [];
    const currentModule = currentModules.find((m) => m?.uuid === moduleUuid);
    if (!currentModule) return; // module itself was removed — nothing to restore into

    const currentSections = currentModule.sections || [];
    // Already back (e.g. re-added via "Add existing section")? Don't duplicate.
    if (
      currentSections.some((s) => s?.uuid && s.uuid === removedSection?.uuid)
    ) {
      return;
    }

    const restoredSections = [...currentSections];
    // Clamp: the list may have shrunk since removal.
    const insertAt = Math.min(sectionIdx, restoredSections.length);
    restoredSections.splice(insertAt, 0, removedSection);
    const restoredModule = { ...currentModule, sections: restoredSections };

    // Persist (removal persisted immediately, so the undo must too), then
    // reflect it locally. Match by UUID in both so a reorder can't misalign it.
    // post_learning_module returns the module on success / undefined on failure
    // (it toasts its own error). On failure, skip the local update so the UI
    // stays consistent with the backend (section stays removed) rather than
    // showing a "restored" section that silently vanishes on reopen.
    const saved = await props.post_learning_module(restoredModule);
    if (!saved) return;

    props.setModalDataTrack((prev) => ({
      ...prev,
      modules: (prev.modules || []).map((m) =>
        m?.uuid === moduleUuid ? restoredModule : m
      ),
    }));

    // Reverse the selection move handleRemoveSectionFromModuleInTrack made — but
    // only if the currently-selected module IS this one (the removal only
    // touched selection when selectedModuleIndex === moduleIdx). Match by UUID
    // since modules may have been reordered.
    const modulesNow = modalDataTrackRef.current?.modules || [];
    const selectedModuleNow = modulesNow[selectedModuleIndexRef.current];
    if (selectedModuleNow?.uuid === moduleUuid) {
      const currentSel = selectedSectionIndexRef.current;
      if (insertAt === currentSel) {
        // Restored section now occupies the selected slot — load it so the
        // highlighted row and the preview agree.
        setSelectedSectionUUID(removedSection?.uuid);
        removeAllBlocks();
        if (removedSection?.uuid) fetchSectionDetail(true, removedSection.uuid);
      } else if (insertAt < currentSel) {
        setSelectedSectionIndex(currentSel + 1);
      }
    }
  };

  // Removes a section from a specific module within the track. Persists
  // immediately because the track Save button only saves track-level fields
  // (name, due date, roles, module order) — module.sections belongs to the
  // module and needs its own POST.
  const handleRemoveSectionFromModuleInTrack = async (
    module,
    moduleIdx,
    sectionIdx
  ) => {
    // Capture before filtering so the undo toast can restore it in place.
    const removedSection = (module.sections || [])[sectionIdx];
    const updatedSections = (module.sections || []).filter(
      (_, i) => i !== sectionIdx
    );
    const updatedModule = { ...module, sections: updatedSections };

    await props.post_learning_module(updatedModule);

    const updatedModules = modules.map((m, i) =>
      i === moduleIdx ? updatedModule : m
    );
    props.setModalDataTrack((prev) => ({ ...prev, modules: updatedModules }));

    if (removedSection) {
      // Capture the module UUID (not the index) — modules can be reordered
      // before the Undo toast is clicked.
      const moduleUuid = module?.uuid;
      const toastId = showUndoToast("Section removed from module", () =>
        undoRemoveSectionFromModuleInTrack(
          moduleUuid,
          sectionIdx,
          removedSection
        )
      );
      undoToastIdsRef.current.push(toastId);
    }

    // Adjust selection if we just removed the currently-selected section.
    if (
      selectedModuleIndex === moduleIdx &&
      selectedSectionIndex === sectionIdx
    ) {
      if (updatedSections.length === 0) {
        setSelectedSectionIndex(0);
        setSelectedSectionUUID(undefined);
        removeAllBlocks();
      } else {
        const newSecIdx = Math.min(
          selectedSectionIndex,
          updatedSections.length - 1
        );
        const uuid = updatedSections[newSecIdx]?.uuid;
        setSelectedSectionIndex(newSecIdx);
        setSelectedSectionUUID(uuid);
        removeAllBlocks();
        if (uuid) fetchSectionDetail(true, uuid);
      }
    } else if (
      selectedModuleIndex === moduleIdx &&
      sectionIdx < selectedSectionIndex
    ) {
      setSelectedSectionIndex(selectedSectionIndex - 1);
    }
  };

  const toggleModuleExpanded = (idx) => {
    setExpandedModuleIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Save / Cancel

  const handleSave = async () => {
    const savedTrack = await props.post_learning_track();
    if (props.onSaveSuccess && !isEdit && savedTrack) {
      props.onSaveSuccess(savedTrack);
    }
    // Clear BlockNote on close. The wrapping component stays mounted
    // across open/close (only the MUI Modal portal toggles), so the
    // editor instance survives. Without this, blocks from this session
    // remain in the document and the next open would render its first
    // section's content *in addition to* the new section — duplicated
    // content in the viewer. Mirrors SectionInputsModal2's pattern.
    removeAllBlocks();
    props.handleClose();
  };

  // Confirm handler for the publish modal: persist the current edits, then
  // enroll. The publish endpoint only receives the track UUID and re-loads
  // the persisted track, so without saving first the modal could promise
  // that newly-selected roles/modules will go live while publishing actually
  // enrolls against the last-saved version. Saving immediately before the
  // enroll keeps what the manager sees, what the modal describes, and what
  // gets enrolled in sync. Skip the enroll if the save failed.
  const handleConfirmPublish = async () => {
    const savedTrack = await props.post_learning_track();
    if (savedTrack) {
      await props.post_learning_track_enrollment_by_roles();
    }
  };

  const handleClose = () => {
    if (originalTrackRef.current) {
      props.setModalDataTrack(originalTrackRef.current);
    }
    setPendingExistingNames([]);
    setAddExistingInput("");
    setAddSectionAnchor(null);
    setTargetModuleForSectionAdd(null);
    setOpenAddExistingSectionsDialog(false);
    setPendingExistingSections([]);
    setAddExistingSectionInput("");
    setSelectedModuleIndex(0);
    setSelectedSectionIndex(0);
    // See handleSave for why this clear is required.
    removeAllBlocks();
    props.handleClose();
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render

  const renderModuleRow = (module, index, dragHandleProps, isDragging) => {
    const isExpanded = expandedModuleIndices.has(index);
    const isSelected = selectedModuleIndex === index;

    return (
      <div className="border-b border-slate-200">
        <div
          onMouseEnter={() => setHoveredModuleIndex(index)}
          onMouseLeave={() => setHoveredModuleIndex(null)}
          onClick={() => toggleModuleExpanded(index)}
          className={`group flex items-center gap-2 pl-1 pr-3 py-3 cursor-pointer transition-colors relative ${
            isDragging
              ? "bg-violet-50 shadow-md"
              : isSelected
              ? "bg-violet-50"
              : "bg-white hover:bg-slate-50"
          }`}
        >
          {isSelected && (
            <div className="h-full w-1 bg-violet-700 absolute left-0 top-0"></div>
          )}
          <div
            className="text-slate-400 cursor-grab hover:text-slate-600"
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon fontSize="small" />
          </div>
          <div className="text-slate-500">
            {isExpanded ? (
              <ExpandMoreIcon style={{ fontSize: "18px" }} />
            ) : (
              <ChevronRightIcon style={{ fontSize: "18px" }} />
            )}
          </div>
          <div
            className={`w-6 h-6 rounded ${
              module.color || "bg-violet-600"
            } flex items-center justify-center shrink-0`}
          >
            <span className="text-white text-xs font-semibold leading-none">
              {index + 1}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <Tooltip title={module.name || ""} placement="right" arrow>
              <p className="text-xs text-slate-900 truncate">{module.name}</p>
            </Tooltip>
            <p className="text-xxs text-slate-500">
              {module.sections?.length || 0}{" "}
              {module.sections?.length === 1 ? "section" : "sections"}
            </p>
          </div>
          {/* Hover actions overlay the title's right edge (with a background
              fade) so the title can use the full row width when not hovered. */}
          <div
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm transition-opacity ${
              hoveredModuleIndex === index
                ? "opacity-100"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditModule(module, index);
              }}
              sx={{
                color: "#6d28d9",
                padding: "3px",
                "&:hover": { backgroundColor: "#f5f3ff" },
              }}
              title="Edit module"
            >
              <EditIcon style={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveModuleFromTrack(index);
              }}
              sx={{
                color: "#94a3b8",
                padding: "3px",
                "&:hover": { backgroundColor: "#fef2f2", color: "#dc2626" },
              }}
              title="Remove from track"
            >
              <CloseIcon style={{ fontSize: 15 }} />
            </IconButton>
          </div>
        </div>

        {isExpanded && (
          <div>
            {(module.sections || []).map((section, sectionIdx) => {
              const sectionKey = `${index}:${sectionIdx}`;
              const isSelectedSection =
                selectedModuleIndex === index &&
                selectedSectionIndex === sectionIdx;
              return (
                <div
                  key={sectionKey}
                  onMouseEnter={() => setHoveredSectionKey(sectionKey)}
                  onMouseLeave={() => setHoveredSectionKey(null)}
                  onClick={() => selectSection(index, sectionIdx, section.uuid)}
                  className={`relative pl-12 pr-3 py-2.5 border-t border-slate-200 cursor-pointer flex items-center justify-between gap-x-2 ${
                    isSelectedSection
                      ? "bg-slate-200"
                      : "bg-slate-50 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <p className="text-xxs text-slate-500 shrink-0">
                      {sectionIdx + 1}.
                    </p>
                    <Tooltip title={section.name || ""} placement="right" arrow>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-900 truncate">
                          {section.name}
                        </p>
                        {section.test_out_quiz && (
                          <p className="text-xxs text-violet-700 truncate">
                            Skips if passed: {section.test_out_quiz.name}
                          </p>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                  <div
                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm transition-opacity ${
                      hoveredSectionKey === sectionKey
                        ? "opacity-100"
                        : "opacity-0 pointer-events-none"
                    }`}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditSection(section, index, sectionIdx);
                      }}
                      sx={{
                        color: "#6d28d9",
                        padding: "3px",
                        "&:hover": { backgroundColor: "#f5f3ff" },
                      }}
                      title="Edit section content"
                    >
                      <EditIcon style={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSectionFromModuleInTrack(
                          module,
                          index,
                          sectionIdx
                        );
                      }}
                      sx={{
                        color: "#94a3b8",
                        padding: "3px",
                        "&:hover": {
                          backgroundColor: "#fef2f2",
                          color: "#dc2626",
                        },
                      }}
                      title="Remove from module"
                    >
                      <CloseIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </div>
                </div>
              );
            })}
            {isEdit && (
              <div className="px-4 py-3 bg-white border-t border-slate-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setTargetModuleForSectionAdd({
                      module,
                      moduleIndex: index,
                    });
                    setAddSectionAnchor(e.currentTarget);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-violet-300 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 transition-colors hover:border-violet-500 hover:bg-violet-100 hover:text-violet-800"
                >
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-violet-700 shadow-sm">
                    <AddIcon style={{ fontSize: "14px" }} />
                  </span>
                  <span>Add Section</span>
                </button>
                <Menu
                  anchorEl={addSectionAnchor}
                  open={
                    Boolean(addSectionAnchor) &&
                    targetModuleForSectionAdd?.moduleIndex === index
                  }
                  onClose={() => {
                    setAddSectionAnchor(null);
                    setTargetModuleForSectionAdd(null);
                  }}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setAddSectionAnchor(null);
                      const target = targetModuleForSectionAdd;
                      setTargetModuleForSectionAdd(null);
                      if (target) {
                        handleCreateSectionInModule(
                          target.module,
                          target.moduleIndex
                        );
                      }
                    }}
                  >
                    <AddIcon fontSize="small" className="mr-2" />
                    Create new section
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAddSectionAnchor(null);
                      setPendingExistingSections([]);
                      setAddExistingSectionInput("");
                      setOpenAddExistingSectionsDialog(true);
                    }}
                  >
                    <CategoryIcon fontSize="small" className="mr-2" />
                    Add existing section
                  </MenuItem>
                </Menu>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        open={props.open}
        aria-labelledby="track-editor-modal"
      >
        <Box sx={modalStyle}>
          {/* = = = Header bar = = = */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 shrink-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <TrackCoverButton
                coverUrl={props.modalDataTrack?.cover?.url}
                uploading={uploadingCover}
                onPick={handleCoverPick}
                onClear={handleCoverClear}
              />
              <div className="min-w-0 flex-1">
                <TextField
                  placeholder={isEdit ? "Track name" : "Untitled track"}
                  value={props.modalDataTrack?.name || ""}
                  onChange={handleNameChange}
                  variant="standard"
                  fullWidth
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: "#0f172a",
                    },
                  }}
                />
                <input
                  type="text"
                  value={props.modalDataTrack?.description || ""}
                  onChange={handleDescriptionChange}
                  placeholder="Add a short description"
                  className="mt-0.5 block w-full bg-transparent border-none px-0 text-sm text-slate-500 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isEdit && (
                isDraft ? (
                  <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold ring-1 ring-amber-300">
                    Draft
                  </span>
                ) : (
                  <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold ring-1 ring-emerald-300">
                    Published
                  </span>
                )
              )}
              <LearningPreviewMenuButton
                variant="button"
                onSelect={(device) => {
                  setPreviewDevice(device);
                  setOpenPreview(true);
                }}
              />
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Save Track
              </button>
              {isEdit && isDraft && (
                <Tooltip title="Review and publish to enroll employees">
                  <button
                    onClick={() => setOpenPublish(true)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    Publish
                  </button>
                </Tooltip>
              )}
              <button
                onClick={handleClose}
                aria-label="Close"
                className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* = = = Body: 3 panes = = = */}
          <div className="flex-1 flex overflow-hidden bg-white">
            {/* - - - Left: modules sidebar - - - */}
            {showMenu && (
              <div className="w-[300px] shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col overflow-hidden">
                <div className="h-14 px-4 border-b border-slate-200 flex items-center justify-between shrink-0">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Track Modules ({moduleCount})
                  </p>
                  <IconButton
                    size="small"
                    onClick={() => setShowMenu(false)}
                    title="Hide sidebar"
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </div>

                <div className="flex-1 overflow-auto bg-white">
                  {moduleCount === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <p className="text-xs text-slate-500">
                        No modules yet. Add one below.
                      </p>
                    </div>
                  ) : (
                    <DragDropContext onDragEnd={handleModuleDragEnd}>
                      <Droppable droppableId="track-modules">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {modules.map((module, index) => (
                              <Draggable
                                key={module.uuid || module.name}
                                draggableId={module.uuid || module.name}
                                index={index}
                              >
                                {(dragProvided, snapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    style={{
                                      ...dragProvided.draggableProps.style,
                                      left: "auto !important",
                                      top: "auto !important",
                                    }}
                                  >
                                    {renderModuleRow(
                                      module,
                                      index,
                                      dragProvided.dragHandleProps,
                                      snapshot.isDragging
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>

                <div className="h-14 border-t border-slate-200 px-3 bg-white flex items-center shrink-0">
                  <button
                    onClick={(e) => setAddModuleAnchor(e.currentTarget)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-violet-300 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 transition-colors hover:border-violet-500 hover:bg-violet-100 hover:text-violet-800"
                  >
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-violet-700 shadow-sm">
                      <AddIcon style={{ fontSize: "14px" }} />
                    </span>
                    <span>Add Module</span>
                  </button>
                  <Menu
                    anchorEl={addModuleAnchor}
                    open={Boolean(addModuleAnchor)}
                    onClose={() => setAddModuleAnchor(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                  >
                    {isEdit ? (
                      <MenuItem
                        onClick={() => {
                          setAddModuleAnchor(null);
                          props.setModalDataModule(props.default_data_module);
                          setIsCreatingNewModule(true);
                          setOpenModuleEditor(true);
                        }}
                      >
                        <AddIcon fontSize="small" className="mr-2" />
                        Create new module
                      </MenuItem>
                    ) : (
                      // New module needs a parent track UUID to attach to,
                      // which only exists after first save. Render disabled
                      // with a tooltip explaining the prerequisite instead
                      // of hiding the option (which left users confused).
                      // MUI requires a non-disabled wrapper for tooltips
                      // over disabled items, hence the <span>.
                      <Tooltip
                        title="Save this track first to create modules here"
                        placement="right"
                      >
                        <span>
                          <MenuItem disabled>
                            <AddIcon fontSize="small" className="mr-2" />
                            Create new module
                          </MenuItem>
                        </span>
                      </Tooltip>
                    )}
                    <MenuItem
                      onClick={() => {
                        setAddModuleAnchor(null);
                        setPendingExistingNames([]);
                        setAddExistingInput("");
                        setOpenAddExistingDialog(true);
                      }}
                    >
                      <CategoryIcon fontSize="small" className="mr-2" />
                      Add existing module
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            )}

            {/* - - - Center: section preview - - - */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
              <div className="h-14 border-b border-slate-200 px-6 flex items-center justify-between bg-white shrink-0">
                {!showMenu && (
                  <IconButton
                    size="small"
                    onClick={() => setShowMenu(true)}
                    title="Show sidebar"
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                )}
                <p className="text-sm text-slate-500 mx-auto">
                  {currentModule
                    ? `Module ${selectedModuleIndex + 1} · Section ${
                        selectedSectionIndex + 1
                      } of ${currentModule.sections?.length || 0}`
                    : "No section selected"}
                </p>
                {!showSettings ? (
                  <IconButton
                    size="small"
                    onClick={() => setShowSettings(true)}
                    title="Show settings"
                  >
                    <TuneIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <div className="w-9" />
                )}
              </div>

              <div className="flex-1 overflow-auto bg-white pt-8">
                {currentSection ? (
                  isLoadingSection ? (
                    <div className="h-full w-full flex items-center justify-center">
                      <LoadSpinner />
                    </div>
                  ) : (
                    // Keyed on the loaded section's uuid so a fresh editor
                    // mounts per section, seeded via initialContent (key and
                    // content come from the same object, so they can't drift).
                    // This is what makes section switching reliable: no shared
                    // editor whose document desyncs across remove/replace cycles
                    // (the old bug that threw "Block with ID … not found").
                    <>
                      <TestOutSkipsNote
                        blocks={props.modalDataSection?.as_json}
                        sections={modules.flatMap((m) => m?.sections || [])}
                      />
                      <SectionPreview
                        key={props.modalDataSection?.uuid}
                        schema={previewSchema}
                        asJson={props.modalDataSection?.as_json}
                      />
                    </>
                  )
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-center px-8">
                    <div>
                      <p className="text-slate-500 text-sm mb-3">
                        This track has no sections yet.
                      </p>
                      <p className="text-slate-400 text-xs">
                        Add a module from the sidebar to start.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {currentSection && (
                <div className="h-14 border-t border-slate-200 px-6 flex items-center justify-between bg-white shrink-0">
                  <button
                    disabled={firstSectionInTrack}
                    onClick={handlePrev}
                    className="py-1.5 px-3 rounded text-sm flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 hover:bg-slate-100"
                  >
                    <ChevronLeftIcon fontSize="small" />
                    Previous
                  </button>
                  <p className="text-xs text-slate-500 truncate px-2">
                    {currentSection.name}
                  </p>
                  <button
                    disabled={lastSectionInTrack}
                    onClick={handleNext}
                    className="py-1.5 px-3 rounded text-sm flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 hover:bg-slate-100"
                  >
                    Next
                    <ChevronRightIcon fontSize="small" />
                  </button>
                </div>
              )}
            </div>

            {/* - - - Right: settings - - - */}
            {showSettings && (
            <div className="w-[340px] shrink-0 border-l border-slate-200 bg-white flex flex-col overflow-hidden">
              <div className="h-14 px-5 border-b border-slate-200 flex items-center justify-between shrink-0">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Track Settings
                </p>
                <IconButton
                  size="small"
                  onClick={() => setShowSettings(false)}
                  title="Hide settings"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </div>

              <div className="flex-1 overflow-auto px-5 py-5 space-y-6">
                {/* Due date */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarTodayIcon
                      className="text-slate-500"
                      style={{ fontSize: "18px" }}
                    />
                    <label className="block text-sm font-medium text-slate-700">
                      Default Due Date
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[
                      { key: "none", label: "No due date" },
                      { key: "fixed", label: "Fixed date" },
                      { key: "relative", label: "Days after assignment" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleDueModeChange(opt.key)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          dueMode === opt.key
                            ? "bg-violet-50 border-violet-300 text-violet-700 font-medium"
                            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {dueMode === "fixed" && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={
                          props.modalDataTrack?.completion_due_date
                            ? new Date(
                                props.modalDataTrack.completion_due_date +
                                  "T00:00:00"
                              )
                            : null
                        }
                        onChange={handleDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            placeholder: "Select a due date",
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                height: "42px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                              },
                            },
                          },
                          actionBar: { actions: ["clear", "accept"] },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                  {dueMode === "relative" && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">Due within</span>
                      <input
                        type="number"
                        min="1"
                        max="3650"
                        value={
                          props.modalDataTrack
                            ?.completion_due_days_after_enrollment ?? ""
                        }
                        onChange={handleDaysChange}
                        onBlur={handleDaysBlur}
                        className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-violet-500 focus:outline-none"
                      />
                      <span className="text-sm text-slate-600">
                        days of assignment
                      </span>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-slate-500">
                    {dueMode === "relative"
                      ? "Each enrollee's deadline is set this many days after they're assigned."
                      : dueMode === "fixed"
                      ? "Default deadline applied to new enrollments."
                      : "Enrollments have no completion deadline."}
                  </p>
                </div>

                {/* Roles */}
                <RoleAssignmentChips
                  roleNames={props.roleNames}
                  selectedRoleIds={selectedRoleIds}
                  onChange={handleRolesChange}
                  companyId={props.homeCompany}
                />

                {/* Badge awarded on completion */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <WorkspacePremiumIcon
                      className="text-slate-500"
                      style={{ fontSize: "18px" }}
                    />
                    <label className="block text-sm font-medium text-slate-700">
                      Badge
                    </label>
                  </div>
                  <Select
                    value={grantsBadge?.uuid || ""}
                    onChange={handleGrantsBadgeChange}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: "8px",
                      fontSize: 14,
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {grantsBadge &&
                      !badgeOptions.some((b) => b.uuid === grantsBadge.uuid) && (
                        <MenuItem value={grantsBadge.uuid} disabled>
                          {/* Stored mapping to a badge the picker no longer
                              offers. The serializer's archived flag is
                              authoritative; before options load we just show
                              the name. Archived badges are never awarded. */}
                          {grantsBadge.archived
                            ? `${grantsBadge.name} (archived, no longer awarded)`
                            : grantsBadge.name}
                        </MenuItem>
                      )}
                    {badgeOptions.map((b) => (
                      <MenuItem key={b.uuid} value={b.uuid}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <p className="mt-2 text-xs text-slate-500">
                    {!grantsBadge
                      ? "Optional: completing this track awards a badge on the employee's profile."
                      : grantsBadge.validity_days
                      ? `The badge expires ${grantsBadge.validity_days} days after earning and renews when the track is re-completed.`
                      : "The badge never expires."}
                  </p>
                  {badgeSharedAcrossTracks && (
                    <p className="mt-1.5 text-xs text-amber-700">
                      This badge is awarded by other tracks too. Completing
                      any of them earns or renews it.
                    </p>
                  )}
                </div>

                {/* Delete */}
                {isEdit && (
                  <div className="pt-4 border-t border-slate-200">
                    <button
                      onClick={() => setOpenDeleteWarning(true)}
                      className="w-full py-2 px-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm flex items-center justify-center gap-2 font-medium transition-colors"
                    >
                      <DeleteIcon fontSize="small" />
                      Delete Track
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </Box>
      </Modal>

      {/* = = = Add existing sections to module dialog = = = */}
      <Dialog
        open={openAddExistingSectionsDialog}
        onClose={() => {
          setOpenAddExistingSectionsDialog(false);
          setTargetModuleForSectionAdd(null);
          setPendingExistingSections([]);
          setAddExistingSectionInput("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add existing sections</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            fullWidth
            value={pendingExistingSections}
            onChange={(_event, newValue) =>
              setPendingExistingSections(newValue)
            }
            inputValue={addExistingSectionInput}
            onInputChange={(_event, newInputValue) =>
              setAddExistingSectionInput(newInputValue)
            }
            disableClearable
            disableCloseOnSelect
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) =>
              option?.uuid === value?.uuid
            }
            options={(props.sectionsData || [])
              .filter((section) => {
                const existingUuids = new Set(
                  (targetModuleForSectionAdd?.module?.sections || []).map(
                    (s) => s.uuid
                  )
                );
                return !existingUuids.has(section.uuid);
              })}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Select sections to add"
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px !important",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            )}
            renderOption={(optionProps, option, { selected }) => {
              const { key, ...otherProps } = optionProps;
              return (
                <li key={key} {...otherProps}>
                  <Checkbox
                    icon={checkboxIcon}
                    checkedIcon={checkboxCheckedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  <div>
                    <p className="text-sm text-slate-900">{option.name}</p>
                    {option.category && (
                      <p className="text-xs text-slate-500">
                        {option.category}
                      </p>
                    )}
                  </div>
                </li>
              );
            }}
            renderTags={() => null}
          />
          {pendingExistingSections.length > 0 && (
            <p className="text-xs text-slate-500 mt-3">
              {pendingExistingSections.length}{" "}
              {pendingExistingSections.length === 1
                ? "section"
                : "sections"}{" "}
              selected
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => {
              setOpenAddExistingSectionsDialog(false);
              setTargetModuleForSectionAdd(null);
              setPendingExistingSections([]);
              setAddExistingSectionInput("");
            }}
            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleAddExistingSectionsToModuleConfirm}
            disabled={pendingExistingSections.length === 0}
            className="px-4 py-2 text-sm bg-violet-700 hover:bg-violet-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add{" "}
            {pendingExistingSections.length > 0
              ? `(${pendingExistingSections.length})`
              : ""}
          </button>
        </DialogActions>
      </Dialog>

      {/* = = = Add existing modules dialog = = = */}
      <Dialog
        open={openAddExistingDialog}
        onClose={() => setOpenAddExistingDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add existing modules</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            fullWidth
            value={pendingExistingNames}
            onChange={(_e, newValue) => setPendingExistingNames(newValue)}
            inputValue={addExistingInput}
            onInputChange={(_e, v) => setAddExistingInput(v)}
            disableClearable
            disableCloseOnSelect
            options={(props.modulesData || []).map((m) => m.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Select modules to add"
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px !important",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            )}
            renderOption={(optionProps, option, { selected }) => {
              const { key, ...otherProps } = optionProps;
              return (
                <li key={key} {...otherProps}>
                  <Checkbox
                    icon={checkboxIcon}
                    checkedIcon={checkboxCheckedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  {option}
                </li>
              );
            }}
            renderTags={() => null}
          />
          {pendingExistingNames.length > 0 && (
            <p className="text-xs text-slate-500 mt-3">
              {pendingExistingNames.length}{" "}
              {pendingExistingNames.length === 1 ? "module" : "modules"}{" "}
              selected
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setOpenAddExistingDialog(false)}
            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleAddExistingModulesConfirm}
            disabled={pendingExistingNames.length === 0}
            className="px-4 py-2 text-sm bg-violet-700 hover:bg-violet-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add{" "}
            {pendingExistingNames.length > 0
              ? `(${pendingExistingNames.length})`
              : ""}
          </button>
        </DialogActions>
      </Dialog>

      {/* = = = Module drill-in (new editor) = = = */}
      <ModuleEditorModal
        open={openModuleEditor}
        handleClose={() => {
          setOpenModuleEditor(false);
          setIsCreatingNewModule(false);
        }}
        modalDataModule={props.modalDataModule}
        setModalDataModule={props.setModalDataModule}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        modalDataSection={props.modalDataSection}
        setModalDataSection={props.setModalDataSection}
        default_data_module={props.default_data_module}
        default_data_section={props.default_data_section}
        sectionsData={props.sectionsData}
        post_learning_module={props.post_learning_module}
        delete_learning_module={props.delete_learning_module}
        post_learning_section={props.post_learning_section}
        delete_learning_section={props.delete_learning_section}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        default_data_quiz={props.default_data_quiz}
        post_learning_quiz={props.post_learning_quiz}
        quizzesData={props.quizzesData}
        modulesData={props.modulesData}
        onSaveSuccess={isCreatingNewModule ? handleNewModuleCreated : null}
      />

      {/* = = = Section drill-in = = = */}
      <SectionInputsModal2
        open={openSectionInputsModal}
        handleClose={() => {
          setOpenSectionInputsModal(false);
          setIsCreatingNewSection(false);
          setTargetModuleForNewSection(null);
        }}
        modalEdit={!isCreatingNewSection}
        isLoading={isLoadingSection}
        modalDataSection={props.modalDataSection}
        setModalDataSection={props.setModalDataSection}
        post_learning_section={props.post_learning_section}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        delete_learning_section={props.delete_learning_section}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        default_data_quiz={props.default_data_quiz}
        post_learning_quiz={props.post_learning_quiz}
        quizzesData={props.quizzesData}
        sectionsData={props.sectionsData}
        modulesData={props.modulesData}
        type="Preview"
        removeAllBlocks={removeAllBlocks}
        refresh_section={refresh_section}
        onSaveSuccess={
          isCreatingNewSection ? handleNewSectionCreatedInModule : null
        }
        parentModuleUuid={targetModuleForNewSection?.module?.uuid}
        hideCategory={true}
      />

      {/* = = = Delete warning = = = */}
      <DeleteWarningModal
        open={openDeleteWarning}
        handleClose={() => setOpenDeleteWarning(false)}
        type="track"
        delete_function={props.delete_learning_track}
        handleCloseInputsModal={() => {
          removeAllBlocks();
          setSelectedModuleIndex(0);
          setSelectedSectionIndex(0);
          props.handleClose();
        }}
      />

      {/* = = = Publish track = = = */}
      <PublishTrackModal
        open={openPublish}
        handleClose={() => setOpenPublish(false)}
        modalDataTrack={props.modalDataTrack}
        post_learning_track_enrollment_by_roles={handleConfirmPublish}
      />
      <LearningContentPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        previewType="track"
        item={props.modalDataTrack}
        initialDevice={previewDevice}
        homeCompany={props.homeCompany}
        quizzesData={props.quizzesData}
      />
    </>
  );
}

// Inline cover-button component. Mirrors SwipeDeckInputsModal's
// DeckCoverButton: dashed-outline placeholder until a file is picked, a
// thumbnail with hover-to-clear once one is set. Replacing the picked
// cover happens by clicking the thumbnail (no need to clear first).
function TrackCoverButton({ coverUrl, uploading, onPick, onClear }) {
  if (uploading) {
    return (
      <div className="w-10 h-10 rounded-lg flex-shrink-0 bg-slate-100 flex items-center justify-center text-xs text-slate-500">
        …
      </div>
    );
  }
  if (coverUrl) {
    return (
      <div className="relative w-10 h-10 flex-shrink-0 group">
        <label className="block w-full h-full rounded-lg overflow-hidden cursor-pointer border border-slate-200">
          <img
            src={coverUrl}
            alt="Track cover"
            className="w-full h-full object-cover"
            draggable={false}
          />
          <input
            type="file"
            accept="image/*"
            onChange={onPick}
            className="hidden"
          />
        </label>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          aria-label="Remove cover"
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <CloseIcon style={{ fontSize: 11 }} />
        </button>
      </div>
    );
  }
  return (
    <Tooltip title="Upload track cover">
      <label className="w-10 h-10 rounded-lg flex-shrink-0 border-2 border-dashed border-slate-300 hover:border-violet-500 hover:bg-violet-50 cursor-pointer flex items-center justify-center text-slate-400 hover:text-violet-600 transition-colors">
        <AddPhotoAlternateOutlinedIcon style={{ fontSize: 20 }} />
        <input
          type="file"
          accept="image/*"
          onChange={onPick}
          className="hidden"
        />
      </label>
    </Tooltip>
  );
}

export default TrackEditorModal;
